import axios, { AxiosInstance } from "axios";
import { jwtDecode } from "jwt-decode";
import {
  AccessPayload,
  AuthResponse,
  BonusBalanceResponse,
  BonusStats,
  BonusTransaction,
  CategoryListResponse,
  CdekCalculateRequest,
  CdekCalculateResponse,
  ClusterInterface,
  ComplexOrderInterface,
  CreateComplexOrderPayload,
  CreatePaymentResponse,
  DocumentLinkInterface,
  ExecutorMeResponse,
  FullAddress,
  InteractionResponse,
  LeadFormData,
  ListByIdsPayload,
  ListParams,
  OrderInterface,
  OrderResponse,
  PaymentMethodsResponse,
  PromoListResponse,
  RateItemDto,
  RegisterEmailPayload,
  SaleItemInterface,
  SaleListResponse,
  ServiceOtpResponse,
  StoreListResponse,
  SyncAction,
  UserInterface,
  UserRating,
  UserViewHistoryItem,
} from "../common/types/types";
import {
  getFromStorage,
  saveToStorage,
  removeFromStorage,
} from "../common/utils/functions/localStorage";
import {
  getAuthUrl,
  getBaseUrl,
  getCacheVersion,
} from "../config/runtimeConfig";
import { setAuthToken } from "../store/slices/authSlice";
import {
  resetCurrentUser,
  setCurrentUser,
} from "../store/slices/currentUserSlice";
import { store } from "../store/store";
import { clearFavorites } from "../store/slices/favoritesSlice";
import { setPaymentMethods } from "../store/slices/paymentMethodsSlice";
import { setBonusData } from "../store/slices/bonusSlice";
import { setOrders } from "../store/slices/orderSlice";
import { clearCart, setCart } from "../store/slices/cartSlice";
import { clearAllActions } from "../store/slices/syncSlice";
import { clearExecutor } from "../store/slices/executorSlice";
import { EntityType } from "../common/types/enums";

let api: Api | null = null;

class Api {
  private readonly axiosInstance: AxiosInstance;
  private store = store;
  private timezone: string;

  constructor(baseURL: string) {
    // 1. Определяем timezone
    this.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

    this.axiosInstance = axios.create({
      baseURL,
      timeout: 30000,
      withCredentials: true,
    });

    this.axiosInstance.interceptors.request.use((config) => {
      // Существующая логика с userId
      const state = store.getState();
      const clientUserId = state.executor.clientUserId;
      const executorData = state.executor.data;

      if (
        (state.executor.isExecutor || state.executor.isAdmin) &&
        executorData?.userId
      ) {
        config.params = {
          ...config.params,
          userId: clientUserId || executorData.userId,
        };
      }

      // Добавляем timezone в заголовки
      config.headers["X-Timezone"] = this.timezone;

      return config;
    });

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Если это запрос на /refresh - не пытаемся рефрешить
        if (originalRequest?.url?.includes("/refresh")) {
          // Чистим ТОЛЬКО если 401
          if (error.response?.status === 401) {
            await this.clearAuthState();
          }
          return Promise.reject(error);
        }

        if (
          originalRequest &&
          error.response?.status === 401 &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;
          try {
            const result = await this.refresh();
            if (result.accessToken) {
              await this.handleAuthResponse(result);
              this.setToken(result.accessToken);
            }
            return this.axiosInstance(originalRequest);
          } catch (refreshError: any) {
            // Чистим ТОЛЬКО если 401
            if (refreshError.response?.status === 401) {
              await this.clearAuthState();
            }
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      },
    );
  }

  private async enhanceUserWithToken(
    user: UserInterface | undefined,
    token: string,
  ): Promise<UserInterface | undefined> {
    if (!user || !token) return user;

    try {
      const decoded: any = jwtDecode(token);

      const enhancedUser = {
        ...user,
        phone_number: decoded.phone_number || user.phone_number,
        userId: decoded.userId || user.id,
        uuid: decoded.uuid || user.uuid,
        contacts: decoded.contacts || user.contacts,
        email: decoded.email || user.email,
      };

      return enhancedUser;
    } catch {
      return user;
    }
  }

  private async clearAuthState(): Promise<void> {
    try {
      const storageKeys = ["accessToken", "refresh", "currentUser"];
      storageKeys.forEach((key) => removeFromStorage(key));

      // Очистка кэшей
      const version = getCacheVersion();
      removeFromStorage(`favorites_v${version}`);
      removeFromStorage(`paymentMethods_v${version}`);
      removeFromStorage(`bonusBalance_v${version}`);
      removeFromStorage(`bonusHistory_v${version}`);
      removeFromStorage(`orders_v${version}`);
      removeFromStorage(`cart_v${version}`);
      removeFromStorage(`sync_pending_v${version}`);

      // Очистка store
      this.store.dispatch(setAuthToken(""));
      this.store.dispatch(resetCurrentUser());
      this.store.dispatch(clearFavorites());
      this.store.dispatch(setPaymentMethods([]));
      this.store.dispatch(
        setBonusData({
          balance: 0,
          totalSpent: 0,
          bonusPercent: 0,
          level: undefined,
        }),
      );
      this.store.dispatch(setOrders([]));
      this.store.dispatch(clearCart());
      this.store.dispatch(clearAllActions());
      this.store.dispatch(clearExecutor());

      delete this.axiosInstance.defaults.headers.common["Authorization"];
    } catch (error) {
      throw error;
    }
  }

  async initAuthState(): Promise<boolean> {
    try {
      const accessToken = getFromStorage<string>("accessToken");
      const refreshToken = getFromStorage<string>("refresh");
      const userStr = getFromStorage<string>("currentUser");

      if (accessToken && userStr) {
        const user: UserInterface = JSON.parse(userStr);

        try {
          const decoded: any = jwtDecode(accessToken);
          const isTokenValid = decoded.exp * 1000 > Date.now();

          if (isTokenValid) {
            const enhancedUser = await this.enhanceUserWithToken(
              user,
              accessToken,
            );

            this.store.dispatch(setCurrentUser(enhancedUser));
            this.store.dispatch(setAuthToken(accessToken));
            this.setToken(accessToken);

            return true;
          } else {
            if (refreshToken) {
              try {
                const result = await this.refresh();
                return !!result;
              } catch {
                await this.clearAuthState();
                return false;
              }
            }
          }
        } catch {
          return false;
        }
      }

      return false;
    } catch {
      return false;
    }
  }

  async setToken(token: string) {
    this.axiosInstance.defaults.headers.common["Authorization"] =
      "Bearer " + token;
  }

  refresh(): Promise<AuthResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        const token = getFromStorage<string>("refresh");
        if (!token) {
          reject(new Error("No refresh token found"));
          return;
        }

        const result = await axios.get<AuthResponse>(
          `${getAuthUrl()}/refresh`,
          {
            headers: {
              Authorization: "Bearer " + token,
            },
            withCredentials: true,
            // ✅ удалён timeout: 5000
          },
        );

        await this.handleAuthResponse(result.data);
        resolve(result.data);
      } catch (error: unknown) {
        reject(error);
      }
    });
  }

  private getUserFromToken(token: string): UserInterface | null {
    try {
      const decoded = jwtDecode<AccessPayload>(token);
      return {
        id: decoded.userId,
        phone_number: decoded.phone_number || "",
        uuid: decoded.uuid || "",
        contacts: decoded.contacts || 0,
        email: decoded.email || "",
      };
    } catch {
      return null;
    }
  }

  botLogin(payload: unknown): Promise<AuthResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await axios.post<AuthResponse>(
          `${getAuthUrl()}/telegram/bot_login`,
          payload,
          {
            withCredentials: true,
            // ✅ удалён timeout: 5000
          },
        );

        await this.handleAuthResponse(result.data);
        resolve(result.data);
      } catch (error: any) {
        reject(error);
      }
    });
  }

  botRegister(payload: unknown): Promise<AuthResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await axios.post<AuthResponse>(
          `${getAuthUrl()}/telegram/bot_register`,
          payload,
          {
            withCredentials: true,
            // ✅ удалён timeout: 5000
          },
        );

        await this.handleAuthResponse(result.data);
        resolve(result.data);
      } catch (error: unknown) {
        reject(error);
      }
    });
  }

  miniAppLogin(payload: unknown): Promise<AuthResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await axios.post<AuthResponse>(
          `${getAuthUrl()}/telegram/mini_app_login`,
          payload,
          {
            withCredentials: true,
            // ✅ удалён timeout: 5000
          },
        );

        await this.handleAuthResponse(result.data);
        resolve(result.data);
      } catch (error: any) {
        reject(error);
      }
    });
  }

  miniAppRegister(payload: unknown): Promise<AuthResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await axios.post<AuthResponse>(
          `${getAuthUrl()}/telegram/mini_app_register`,
          payload,
          {
            withCredentials: true,
            // ✅ удалён timeout: 5000
          },
        );

        await this.handleAuthResponse(result.data);
        resolve(result.data);
      } catch (error: unknown) {
        reject(error);
      }
    });
  }

  logout(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        const refreshToken = localStorage.getItem("refresh"); // ← было: this.store.getState().auth.token

        if (refreshToken) {
          await axios.get<string>(`${getAuthUrl()}/logout`, {
            headers: {
              Authorization: "Bearer " + refreshToken, // ← было: token
            },
            withCredentials: true,
          });
        }

        await this.clearAuthState();
        resolve();
      } catch (error: unknown) {
        await this.clearAuthState();
        reject(error);
      }
    });
  }

  async getSaleItemList(
    params?: ListParams,
  ): Promise<SaleListResponse | undefined> {
    try {
      const result = await this.axiosInstance.get<SaleListResponse>(
        "/sale_item/app",
        { params },
      );
      if (result) return result.data;
      return undefined;
    } catch (err) {
      throw err;
    }
  }

  async getSaleItemListByIds(
    payload: ListByIdsPayload,
  ): Promise<SaleListResponse | undefined> {
    try {
      const result = await this.axiosInstance.post<SaleListResponse>(
        "/sale_item/list-by-ids",
        payload,
      );
      if (result) return result.data;
      return undefined;
    } catch (err) {
      throw err;
    }
  }

  async getCategoryList(
    params?: ListParams,
  ): Promise<CategoryListResponse | undefined> {
    try {
      const result = await this.axiosInstance.get<CategoryListResponse>(
        "/category/app",
        { params },
      );

      if (result) return result.data;
      return undefined;
    } catch (err) {
      throw err;
    }
  }

  async getStoreList(
    params?: ListParams,
  ): Promise<StoreListResponse | undefined> {
    try {
      const result = await this.axiosInstance.get<StoreListResponse>("/store", {
        params,
      });
      if (result) return result.data;
      return undefined;
    } catch (err) {
      throw err;
    }
  }

  async getAddressList(
    params?: ListParams,
  ): Promise<FullAddress[] | undefined> {
    try {
      const result = await this.axiosInstance.get<FullAddress[]>(
        "/full_address",
        { params },
      );
      if (result) return result.data;
      return undefined;
    } catch (err) {
      throw err;
    }
  }

  async getPromoList(
    params?: ListParams,
  ): Promise<PromoListResponse | undefined> {
    try {
      const result = await this.axiosInstance.get<PromoListResponse>("/promo", {
        params,
      });
      if (result) return result.data;
      return undefined;
    } catch (err) {
      throw err;
    }
  }

  async getOrderList(
    params?: ListParams,
  ): Promise<OrderInterface[] | undefined> {
    try {
      const result = await this.axiosInstance.get<OrderInterface[]>(
        "/order/app",
        { params },
      );

      if (result) return result.data;
      return undefined;
    } catch (err) {
      throw err;
    }
  }

  async getCluster(timeout?: number): Promise<ClusterInterface | undefined> {
    try {
      const config = timeout ? { timeout } : {};
      const result = await this.axiosInstance.get<ClusterInterface>(
        "/cluster/self",
        config,
      );
      if (result) return result.data;
      return undefined;
    } catch (err) {
      throw err;
    }
  }

  async healthCheck(timeout?: number): Promise<void> {
    const config = timeout ? { timeout } : {};
    await this.axiosInstance.get("/health", {
      ...config,
      headers: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    });
  }

  async createAddress(payload: FullAddress): Promise<FullAddress | undefined> {
    try {
      const result = await this.axiosInstance.post(
        "/full_address",
        payload,
        {},
      );
      if (result) return result.data;
      return undefined;
    } catch (err) {
      throw err;
    }
  }

  async createOrder(
    payload: OrderInterface,
  ): Promise<OrderResponse | undefined> {
    try {
      const result = await this.axiosInstance.post("/order/web", payload, {});
      if (result) return result.data;
      return undefined;
    } catch (error: any) {
      console.error("💥 [FRONT] Order error:", {
        status: error.response?.status,
        message: error.response?.data?.message,
        error: error.response?.data?.error,
        details: error.response?.data?.details,
      });
      throw error;
    }
  }

  async deleteAddress(id: number): Promise<string | undefined> {
    try {
      const result = await this.axiosInstance.delete<string>(
        `/full_address/${id}`,
      );
      if (result) return result.data;
      return undefined;
    } catch (err) {
      throw err;
    }
  }

  async setDefaultAddress(id: number): Promise<string | undefined> {
    try {
      const result = await this.axiosInstance.put<string>(
        `/full_address/set-default/${id}`,
      );
      if (result) return result.data;
      return undefined;
    } catch (err) {
      throw err;
    }
  }

  async getUserRatings(params?: {
    limit?: number;
    offset?: number;
    sortBy?: "createdAt" | "rating" | "itemName";
    sortOrder?: "ASC" | "DESC";
  }): Promise<{ items: UserRating[]; total: number } | undefined> {
    try {
      const result = await this.axiosInstance.get<{
        items: UserRating[];
        total: number;
      }>("/sale_item/ratings/my", { params });
      return result.data;
    } catch (error) {
      console.error("Error fetching user ratings:", error);
      throw error;
    }
  }

  async deleteRating(itemId: number): Promise<InteractionResponse | undefined> {
    try {
      const result = await this.axiosInstance.delete<InteractionResponse>(
        `/sale_item/${itemId}/rating`,
      );
      return result.data;
    } catch (error) {
      console.error("Error deleting rating:", error);
      throw error;
    }
  }

  async getViewHistory(params?: {
    limit?: number;
    offset?: number;
    sortBy?: "viewedAt" | "itemPrice" | "itemName";
    sortOrder?: "ASC" | "DESC";
  }): Promise<{ items: UserViewHistoryItem[]; total: number } | undefined> {
    try {
      const result = await this.axiosInstance.get<{
        items: UserViewHistoryItem[];
        total: number;
      }>("/sale_item/views/history", { params });
      return result.data;
    } catch (error) {
      console.error("Error fetching view history:", error);
      throw error;
    }
  }

  // async getUserFavorites(params?: {
  //   limit?: number;
  //   offset?: number;
  //   sortBy?: "selectedAt" | "itemPrice" | "itemName";
  //   sortOrder?: "ASC" | "DESC";
  // }): Promise<{ items: FavoriteItemInterface[]; total: number } | undefined> {
  //   try {
  //     const result = await this.axiosInstance.get<{
  //       items: FavoriteItemInterface[];
  //       total: number;
  //     }>("/sale_item/favorites", { params });
  //     return result.data;
  //   } catch (error) {
  //     console.error("Error fetching favorites:", error);
  //     throw error;
  //   }
  // }

  // async getFavoritesStatus(
  //   itemIds: number[],
  // ): Promise<Map<number, boolean> | undefined> {
  //   try {
  //     const response = await this.getUserFavorites({ limit: 1000 });
  //     if (!response) return undefined;

  //     const statusMap = new Map<number, boolean>();
  //     const favoriteItemIds = response.items.map((fav) => fav.itemId);

  //     itemIds.forEach((itemId) => {
  //       statusMap.set(itemId, favoriteItemIds.includes(itemId));
  //     });

  //     return statusMap;
  //   } catch (error) {
  //     console.error("Error fetching favorites status:", error);
  //     throw error;
  //   }
  // }

  async getUserFavorites(params?: {
    limit?: number;
    offset?: number;
    search?: string;
  }): Promise<SaleListResponse | undefined> {
    try {
      const result = await this.axiosInstance.get<SaleListResponse>(
        "/sale_item/favorites",
        { params },
      );
      if (result) return result.data;
      return undefined;
    } catch (err) {
      throw err;
    }
  }

  async syncInteractions(
    actions: SyncAction[],
  ): Promise<InteractionResponse | undefined> {
    try {
      const result = await this.axiosInstance.post<InteractionResponse>(
        "/sale_item/interactions/sync",
        { actions },
      );
      return result.data;
    } catch (error) {
      console.error("Error syncing interactions:", error);
      throw error;
    }
  }

  async trackViewsBatch(
    itemIds: number[],
  ): Promise<InteractionResponse | undefined> {
    try {
      const result = await this.axiosInstance.post<InteractionResponse>(
        "/sale_item/views/batch",
        itemIds,
      );
      return result.data;
    } catch (error: any) {
      console.error("❌ [API] Ошибка trackViewsBatch:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        itemIds,
      });
      throw error;
    }
  }

  async rateItem(
    itemId: number,
    ratingData: RateItemDto,
  ): Promise<InteractionResponse | undefined> {
    try {
      const result = await this.axiosInstance.post<InteractionResponse>(
        `/sale_item/${itemId}/rate`,
        ratingData,
      );
      return result.data;
    } catch (error: any) {
      console.error("❌ [API] Ошибка rateItem:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        itemId,
        ratingData,
      });
      throw error;
    }
  }

  async trackView(itemId: number): Promise<InteractionResponse | undefined> {
    try {
      const result = await this.axiosInstance.post<InteractionResponse>(
        `/sale_item/${itemId}/view`,
      );
      return result.data;
    } catch (error: any) {
      console.error("❌ [API] Ошибка trackView:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        itemId,
      });
      throw error;
    }
  }

  async toggleFavorite(
    itemId: number,
  ): Promise<InteractionResponse | undefined> {
    try {
      const result = await this.axiosInstance.post<InteractionResponse>(
        `/sale_item/${itemId}/favorite`,
      );
      return result.data;
    } catch (error: any) {
      console.error("❌ [API] Ошибка toggleFavorite:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        itemId,
      });
      throw error;
    }
  }

  async getBonusBalance(): Promise<BonusBalanceResponse> {
    try {
      const result =
        await this.axiosInstance.get<BonusBalanceResponse>("/bonus/balance");
      return result.data;
    } catch (err) {
      console.error("❌ [API] Error fetching bonus balance:", err);
      throw err;
    }
  }

  async getBonusHistory(
    limit = 50,
    offset = 0,
  ): Promise<BonusTransaction[] | undefined> {
    try {
      const result = await this.axiosInstance.get("/bonus/history", {
        params: { limit, offset },
      });

      return result?.data;
    } catch (err) {
      console.error("Error fetching bonus history:", err);
      throw err;
    }
  }

  async getBonusStats(): Promise<BonusStats | undefined> {
    try {
      const result = await this.axiosInstance.get<BonusStats>("/bonus/stats");
      if (result) return result.data;
      return undefined;
    } catch (err) {
      console.error("Error fetching bonus stats:", err);
      throw err;
    }
  }

  async getOrder(
    id: number,
    timeout?: number,
  ): Promise<OrderInterface | undefined> {
    try {
      const result = await this.axiosInstance.get<OrderInterface>(
        `/order/app/${id}`,
      );
      if (result) return result.data;
      return undefined;
    } catch (err) {
      throw err;
    }
  }

  async requestEmailCode(payload: {
    email: string;
    clusterId?: number;
  }): Promise<{ message: string }> {
    try {
      const response = await axios.post(
        `${getAuthUrl()}/request-code`,
        payload,
        {
          withCredentials: true,
          // ✅ удалён timeout: 5000
        },
      );
      return response.data;
    } catch (err) {
      console.error("❌ [API] Error requesting email code:", err);
      throw err;
    }
  }

  async verifyEmailCode(payload: {
    email: string;
    code: string;
  }): Promise<AuthResponse> {
    try {
      const response = await axios.post<AuthResponse>(
        `${getAuthUrl()}/verify-code`,
        payload,
        {
          withCredentials: true,
          // ✅ удалён timeout: 5000
        },
      );

      await this.handleAuthResponse(response.data);

      return response.data;
    } catch (err: any) {
      console.error("❌ [API] Error verifying email code:", {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
        config: {
          url: err.config?.url,
          method: err.config?.method,
          data: err.config?.data,
        },
      });
      throw err;
    }
  }

  private async handleAuthResponse(response: AuthResponse): Promise<void> {
    if (!response.accessToken || !response.refreshToken) return;

    const userFromToken = this.getUserFromToken(response.accessToken);

    if (userFromToken) {
      saveToStorage("currentUser", JSON.stringify(userFromToken));
      store.dispatch(setCurrentUser(userFromToken));
    }

    saveToStorage("accessToken", response.accessToken);
    saveToStorage("refresh", response.refreshToken);
    store.dispatch(setAuthToken(response.accessToken));
    this.setToken(response.accessToken);
  }

  register(payload: RegisterEmailPayload): Promise<AuthResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await axios.post<AuthResponse>(
          `${getAuthUrl()}/register`,
          payload,
          {
            withCredentials: true,
            // ✅ удалён timeout: 5000
          },
        );

        await this.handleAuthResponse(result.data);
        resolve(result.data);
      } catch (error: any) {
        reject(error);
      }
    });
  }

  async requestAttachEmailCode(payload: {
    email: string;
    clusterId?: number;
  }): Promise<{ message: string }> {
    try {
      const token = getFromStorage<string>("accessToken");

      const response = await axios.post(
        `${getAuthUrl()}/request-attach-code`,
        payload,
        {
          headers: {
            Authorization: "Bearer " + token,
          },
          withCredentials: true,
          // ✅ удалён timeout: 5000
        },
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async getPaymentMethods(): Promise<PaymentMethodsResponse> {
    try {
      const result =
        await this.axiosInstance.get<PaymentMethodsResponse>(
          "/payment/methods",
        );
      return result.data;
    } catch (error: any) {
      console.error("❌ [API] Ошибка getPaymentMethods:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async deletePaymentMethod(id: number): Promise<void> {
    try {
      await this.axiosInstance.delete(`/payment/methods/${id}`);
    } catch (error: any) {
      console.error("❌ [API] Ошибка deletePaymentMethod:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        id,
      });
      throw error;
    }
  }

  async setDefaultPaymentMethod(id: number): Promise<void> {
    try {
      await this.axiosInstance.post(`/payment/methods/${id}/default`);
    } catch (error: any) {
      console.error("❌ [API] Ошибка setDefaultPaymentMethod:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        id,
      });
      throw error;
    }
  }

  async createPayment(data: {
    price: number;
    quantity: number;
    description: string;
    orderId?: number;
    savePaymentMethod?: boolean;
  }): Promise<CreatePaymentResponse> {
    try {
      const result = await this.axiosInstance.post<CreatePaymentResponse>(
        "/payment/yookassa/web",
        data,
      );
      return result.data;
    } catch (error: any) {
      console.error("❌ [API] Ошибка createPayment:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async getItemById(id: number): Promise<SaleItemInterface | undefined> {
    try {
      const result = await this.axiosInstance.get<SaleItemInterface>(
        `/sale_item/${id}`,
      );
      return result.data;
    } catch (err) {
      throw err;
    }
  }

  async getGroupItems(
    groupId: number,
  ): Promise<SaleItemInterface[] | undefined> {
    try {
      const result = await this.axiosInstance.get<SaleItemInterface[]>(
        `/sale_item/group/${groupId}`,
      );
      return result.data;
    } catch (err) {
      throw err;
    }
  }

  async createComplexOrder(payload: CreateComplexOrderPayload): Promise<any> {
    try {
      const result = await this.axiosInstance.post(
        "/complex-order",
        payload,
        {},
      );
      return result.data;
    } catch (error: any) {
      console.error("💥 [FRONT] Complex order error:", {
        status: error.response?.status,
        message: error.response?.data?.message,
      });
      throw error;
    }
  }

  async getComplexOrderList(): Promise<ComplexOrderInterface[]> {
    try {
      const result = await this.axiosInstance.get("/complex-order/app");
      if (result) return result.data;
      return [];
    } catch (error: any) {
      console.error("💥 [FRONT] Get complex order list error:", {
        status: error.response?.status,
        message: error.response?.data?.message,
      });
      throw error;
    }
  }

  async getComplexOrderById(
    id: string,
  ): Promise<ComplexOrderInterface | undefined> {
    try {
      const result = await this.axiosInstance.get(`/complex-order/app/${id}`);
      if (result) return result.data;
      return undefined;
    } catch (error: any) {
      console.error("💥 [FRONT] Get complex order by id error:", {
        status: error.response?.status,
        message: error.response?.data?.message,
      });
      throw error;
    }
  }

  async getServiceOtp(): Promise<ServiceOtpResponse | undefined> {
    try {
      const result =
        await this.axiosInstance.get<ServiceOtpResponse>("/service/otp");
      if (result) return result.data;
      return undefined;
    } catch (error: any) {
      console.error("💥 [FRONT] Get service otp error:", {
        status: error.response?.status,
        message: error.response?.data?.message,
      });
      throw error;
    }
  }

  async useServiceOtp(code: string): Promise<{ userId: number } | undefined> {
    try {
      const result = await this.axiosInstance.post("/service/otp/use", {
        code,
      });
      if (result) return result.data;
      return undefined;
    } catch (error: any) {
      console.error("💥 [FRONT] Use service otp error:", {
        status: error.response?.status,
        message: error.response?.data?.message,
      });
      throw error;
    }
  }

  async getExecutorMe(): Promise<ExecutorMeResponse | undefined> {
    try {
      const result =
        await this.axiosInstance.get<ExecutorMeResponse>("/executor/me");
      if (result) return result.data;
      return undefined;
    } catch (error: any) {
      console.error("💥 [FRONT] Get executor me error:", {
        status: error.response?.status,
        message: error.response?.data?.message,
      });
      throw error;
    }
  }

  async startTransition(
    orderId: number,
    durationMinutes: number,
  ): Promise<any> {
    try {
      const result = await this.axiosInstance.post(
        `/order/${orderId}/start-transition`,
        {
          durationMinutes,
        },
      );
      return result.data;
    } catch (error: any) {
      console.error("💥 [FRONT] Start transition error:", {
        status: error.response?.status,
        message: error.response?.data?.message,
      });
      throw error;
    }
  }

  async updateOrderStatusByExecutor(
    orderId: number,
    status: number,
  ): Promise<void> {
    try {
      await this.axiosInstance.put(`/order/${orderId}/status`, { status });
    } catch (error: any) {
      console.error("💥 [FRONT] Update order status error:", {
        status: error.response?.status,
        message: error.response?.data?.message,
      });
      throw error;
    }
  }

  async updateOrderPaymentStatus(
    orderId: number,
    status: number,
  ): Promise<void> {
    try {
      await this.axiosInstance.put(`/order/${orderId}/payment-status`, {
        status,
      });
    } catch (error: any) {
      console.error("💥 [FRONT] Update payment status error:", {
        status: error.response?.status,
        message: error.response?.data?.message,
      });
      throw error;
    }
  }

  async sendLead(payload: LeadFormData): Promise<{ success: boolean }> {
    try {
      const result = await this.axiosInstance.post("/mail/send-lead", payload);
      return result.data;
    } catch (error: any) {
      throw error;
    }
  }

  async cdekCalculate(
    payload: CdekCalculateRequest,
  ): Promise<CdekCalculateResponse> {
    try {
      const result = await this.axiosInstance.post("/cdek/calculate", payload);
      return result.data;
    } catch (error: any) {
      console.error("💥 [FRONT] CDEK calculate error:", {
        status: error.response?.status,
        message: error.response?.data?.message,
      });
      throw error;
    }
  }

  async cdekGetPickupPoints(cityCode: number): Promise<any> {
    try {
      const result = await this.axiosInstance.get(
        `/cdek/pickup-points?cityCode=${cityCode}`,
      );
      return result.data;
    } catch (error: any) {
      console.error("💥 [FRONT] CDEK pickup points error:", {
        status: error.response?.status,
        message: error.response?.data?.message,
      });
      throw error;
    }
  }
  
  async getEntityDocumentFileListPublic(params: {
    entityType: EntityType;
    entityId?: number;
  }): Promise<DocumentLinkInterface[]> {
    try {
      const result = await this.axiosInstance.get<DocumentLinkInterface[]>(
        "/document/app",
        { params },
      );
      return result.data;
    } catch (err) {
      throw err;
    }
  }
}

export const getApi = () => {
  if (!api) api = new Api(getBaseUrl());
  return api;
};

export const initAppAuth = async (): Promise<boolean> => {
  try {
    const apiInstance = getApi();
    return await apiInstance.initAuthState();
  } catch {
    return false;
  }
};
